import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { LoginFormFieldsType } from './types'
import { EMAIL } from 'constants/regex'
import { login } from 'store/auth/thunks'
import { useLocation, useNavigate } from 'react-router'
import { routes } from 'types/routes'
import styles from '../Auth.module.scss'
import { Banner, Button, Form, Heading, Password, Tag, TextField } from '@boltenergy-be/design-system'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { checkIsDown } from 'utils/app.ts'
import Link from 'components/link/Link'
import { getUserTypeHomepage } from 'utils/user.ts'
import parse from 'html-react-parser'
import { IS_BETA } from 'constants/envs.ts'
import { determineAuthUserType } from 'pages/auth/utils.ts'
import { UserTypes } from 'store/auth/types.ts'
import { setUserType } from 'utils/userType.ts'
import { trackEvent } from 'utils/tracking.ts'
import { useGetLanguage } from 'hooks/use-get-language'

const Login = () => {
  // REDUX STORE
  const { error, loading } = useStoreSelector((store) => store.auth)
  const dispatch = useStoreDispatch()

  // React hook form
  const {
    handleSubmit,
    formState: { errors },
    register
  } = useForm<LoginFormFieldsType>({ mode: 'onChange' })

  // i18n
  const { t } = useTranslation(['auth', 'validation'])
  const language = useGetLanguage()

  // React router
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Constants
  const userType = determineAuthUserType(pathname)
  const { isDown, isApproaching, message } = checkIsDown(userType)

  // Apply the correct user type
  useEffect(() => {
    setUserType(userType)
  }, [userType])

  /**
   * Changes the page title
   */
  useEffect(() => {
    if (userType === UserTypes.SALES) {
      document.title = 'Bolt Sales Portal'
    }
  }, [userType])

  /**
   * Handles the submit after the validation
   *
   * @param {LoginFormFieldsType} data
   */
  const onSubmit = async (data: LoginFormFieldsType) => {
    const { email, password } = data

    trackEvent({ action: 'submit', category: 'auth', object: 'login' })

    const result = await dispatch(login({ email: email.toLowerCase(), password, userType }))

    if (result.meta.requestStatus === 'fulfilled') {
      void navigate(getUserTypeHomepage(userType))
    }
  }

  return (
    <>
      <div className={styles['auth-body']}>
        <header className={styles.header}>
          <div className={styles.heading}>
            <Heading as="h1" variant="h3">
              {t(($) => $.titles.login)}
            </Heading>
            {userType !== UserTypes.CUSTOMER && <Tag type="warning">{t(($) => $.tags[userType])}</Tag>}
          </div>
          {userType !== UserTypes.SUPER_USER && <p className={styles.description}>{t(($) => $.descriptions.login[userType])}</p>}
        </header>

        {(isDown || isApproaching) && (
          <Banner type="informative" className={styles.maintenance}>
            <p>{message}</p>
          </Banner>
        )}

        {!isDown && (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register('email', {
                required: t(($) => $.required, {
                  ns: 'validation'
                }),
                pattern: {
                  value: EMAIL,
                  message: t(($) => $.invalid.email, {
                    ns: 'validation'
                  })
                }
              })}
              error={errors?.email?.message}
              label={t(($) => $.fields.email)}
              type="email"
            />

            <Password
              {...{ language }}
              {...register('password', {
                required: t(($) => $.required, {
                  ns: 'validation'
                })
              })}
              error={errors?.password?.message}
              label={t(($) => $.fields.password)}
              labelComponentSlot={
                !IS_BETA && (
                  <Link
                    variant="primary"
                    href={
                      userType === UserTypes.SALES
                        ? routes.REQUEST_NEW_PASSWORD_SALES
                        : userType === UserTypes.SUPER_USER
                          ? routes.REQUEST_NEW_PASSWORD_SUPER_USER
                          : routes.REQUEST_NEW_PASSWORD
                    }
                  >
                    {t(($) => $.buttons.forgotPassword)}
                  </Link>
                )
              }
            />

            <Button isFullwidth loading={loading}>
              {t(($) => $.buttons.login)}
            </Button>
          </Form>
        )}

        {error && (
          <Banner className={styles.banner} type="blocking">
            {t(($) => $.messages.loginError)}
          </Banner>
        )}
      </div>

      {userType === UserTypes.CUSTOMER && !IS_BETA && (
        <footer className={styles['auth-footer']}>
          <p>{parse(t(($) => $.subSections.notRegistered.text))}</p>
          <Link className={styles['auth-link']} href={routes.REGISTER} representation="button" isFullwidth variant="secondary">
            {t(($) => $.subSections.notRegistered.button)}
          </Link>
        </footer>
      )}
    </>
  )
}

export default Login
